/**
 * Europe/Prague-anchored date & time helpers.
 *
 * Dates are written the way the user's format says (`20. 10. 2026` or
 * `Oct 20, 2026`), on a 24-hour clock, and — where an instant is involved —
 * in the Europe/Prague timezone, which nobody can change.
 *
 * Two string shapes flow through the custom pickers, deliberately the
 * same shapes the native `<input type="date|datetime-local">` used so
 * callsites swap the control without reworking their state:
 *   - a *wall date*     — `YYYY-MM-DD`           (no timezone)
 *   - a *wall datetime* — `YYYY-MM-DDTHH:mm`     (Prague wall-clock)
 *
 * A wall datetime only becomes an absolute instant when it is paired
 * with a zone — `pragueWallToIso` does that, `isoToPragueWall` is the
 * inverse. Wall dates carry no zone and need no conversion.
 */

import {
  DISPLAY_TZ,
  type Locale,
  formatDate,
  formatDateTime,
  getPreferences,
  plainSpaces,
} from "./i18n";

export const PRAGUE_TZ = DISPLAY_TZ;

const pad = (n: number) => String(n).padStart(2, "0");

/** Wall-clock components of an absolute instant, read in Prague. */
function partsInPrague(d: Date): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: PRAGUE_TZ,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const m: Record<string, string> = {};
  for (const p of fmt.formatToParts(d)) m[p.type] = p.value;
  return {
    year: Number(m.year),
    month: Number(m.month),
    day: Number(m.day),
    hour: Number(m.hour),
    minute: Number(m.minute),
    second: Number(m.second),
  };
}

/** Prague's UTC offset (ms) at the given instant — DST-aware. */
function pragueOffsetMs(d: Date): number {
  const p = partsInPrague(d);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - d.getTime();
}

/**
 * Interpret a `YYYY-MM-DDTHH:mm` wall datetime as Prague local time
 * and return the matching absolute instant as a UTC ISO string.
 * Returns `""` for empty/invalid input.
 */
export function pragueWallToIso(wall: string): string {
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const h = Number(m[4]);
  const mi = Number(m[5]);
  // One Newton step is enough away from the 1-hour DST seam: guess the
  // instant as if the wall time were UTC, read Prague's offset there,
  // then correct by it.
  const utcGuess = Date.UTC(y, mo - 1, da, h, mi);
  const offset = pragueOffsetMs(new Date(utcGuess));
  return new Date(utcGuess - offset).toISOString();
}

/**
 * Inverse of `pragueWallToIso`: render an absolute ISO instant as the
 * `YYYY-MM-DDTHH:mm` Prague wall datetime a picker can edit.
 */
export function isoToPragueWall(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = partsInPrague(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** The Prague calendar date (`YYYY-MM-DD`) an ISO instant falls on. */
export function isoToPragueDate(iso: string): string {
  return isoToPragueWall(iso).slice(0, 10);
}

// --- Display formatting (user's date format) --------------------------------

/** `YYYY-MM-DD` → `20. 10. 2026` / `Oct 20, 2026`. Empty/invalid input → `""`. */
export function formatWallDate(isoDate: string, format: Locale = getPreferences().format): string {
  const m = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return formatDate(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])), format, "UTC");
}

/** `YYYY-MM-DDTHH:mm` → `20. 10. 2026 14:30` / `Oct 20, 2026, 14:30`. Empty/invalid → `""`. */
export function formatWallDateTime(wall: string, format: Locale = getPreferences().format): string {
  const m = wall.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return "";
  return formatDateTime(
    Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])),
    format,
    "UTC",
  );
}

// --- Parsing ----------------------------------------------------------------

/** True when `y-m-d` is a real calendar date. */
function isRealDate(y: number, mo: number, da: number): boolean {
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return false;
  const d = new Date(y, mo - 1, da);
  return d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === da;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/** Day, month, year out of `20. 10. 2026` or `Oct 20, 2026` — whichever is typed. */
function matchDate(text: string): { y: number; mo: number; da: number; rest: string } | null {
  const t = plainSpaces(text).trim();
  const cz = t.match(/^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})(.*)$/);
  if (cz) return { da: Number(cz[1]), mo: Number(cz[2]), y: Number(cz[3]), rest: cz[4] ?? "" };
  const en = t.match(/^([A-Za-z]{3})[A-Za-z]*\.?\s+(\d{1,2}),?\s+(\d{4})(.*)$/);
  const mo = en ? MONTHS[(en[1] ?? "").toLowerCase()] : undefined;
  if (en && mo) return { da: Number(en[2]), mo, y: Number(en[3]), rest: en[4] ?? "" };
  return null;
}

/** `20. 10. 2026` / `Oct 20, 2026` (lenient on leading zeros) → `YYYY-MM-DD`, or `null`. */
export function parseWallDate(text: string): string | null {
  const d = matchDate(text);
  if (!d || d.rest.trim() !== "" || !isRealDate(d.y, d.mo, d.da)) return null;
  return `${d.y}-${pad(d.mo)}-${pad(d.da)}`;
}

/** `20. 10. 2026 14:30` / `Oct 20, 2026, 14:30` → `YYYY-MM-DDTHH:mm`, or `null`. */
export function parseWallDateTime(text: string): string | null {
  const d = matchDate(text);
  if (!d) return null;
  const t = d.rest.match(/^[ ,]+(\d{1,2}):(\d{2})$/);
  if (!t) return null;
  const h = Number(t[1]);
  const mi = Number(t[2]);
  if (!isRealDate(d.y, d.mo, d.da) || h > 23 || mi > 59) return null;
  return `${d.y}-${pad(d.mo)}-${pad(d.da)}T${pad(h)}:${pad(mi)}`;
}

/**
 * Lenient parser for the `DateTimePicker` input — accepts both a date with
 * a time and a bare date. Date-only input defaults to `00:00`, which is the
 * sensible boundary for filters that mean "since this day" / "up to and
 * including this day's start".
 */
export function parseWallDateOrDateTime(text: string): string | null {
  const full = parseWallDateTime(text);
  if (full) return full;
  const dateOnly = parseWallDate(text);
  if (dateOnly) return `${dateOnly}T00:00`;
  return null;
}

// --- Calendar grid helpers --------------------------------------------------

/**
 * The 42 days (6 weeks, Monday-first) that fill a month's calendar
 * grid — including the trailing days of the previous month and the
 * leading days of the next so every week is complete.
 */
export function monthGridDays(year: number, monthIndex: number): Date[] {
  const first = new Date(year, monthIndex, 1);
  // JS getDay(): 0=Sun … 6=Sat. Monday-first offset.
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, monthIndex, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

/** `Date` → `YYYY-MM-DD` using its local calendar fields. */
export function dateToWallDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Today's Prague calendar date as `YYYY-MM-DD`. */
export function todayPragueDate(): string {
  return isoToPragueDate(new Date().toISOString());
}

/**
 * The absolute instant Prague's current day began — the line between time
 * that can be confirmed and time that is still being worked.
 *
 * Prague rather than the browser's zone so a laptop travelling with someone
 * doesn't move the working day, and so the answer matches the server's
 * (`server/lib/workday.ts`), which is where the rule is actually enforced.
 */
export function startOfTodayPrague(): Date {
  return new Date(pragueWallToIso(`${todayPragueDate()}T00:00`));
}
